import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { OrderDateSelect } from './PastOrderSearch';

const OTHER_TEACHER = '__other__';

// Modal for an admin to correct the content of an order already placed
// (e.g. a parent picked the wrong grade). Deliberately does not touch
// paid/status/deletion — those already have their own dedicated actions
// on the Orders page.
//
// With mode="copy" the same form instead creates a *new* order pre-filled
// from `order` (see CopyOrder.js): it adds an order-date picker (from
// `orderDates`, starting at `defaultOrderDateId`) and a send-confirmation-
// email checkbox, and POSTs to /orders rather than updating `order`.
// onSaved receives the saved order in both modes.
function EditOrderModal({ apiBase, order, onClose, onSaved, mode = 'edit', orderDates = [], defaultOrderDateId = '' }) {
  const isCopy = mode === 'copy';
  const [orderDateId, setOrderDateId] = useState(String(defaultOrderDateId));
  const [sendEmail, setSendEmail] = useState(!!order.email);
  const [options, setOptions] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isOtherTeacher = order.teacher && !teachers.some(t => t.name === order.teacher);

  const [form, setForm] = useState({
    name: order.name || '',
    grade: order.grade || '',
    teacher: order.teacher || '',
    otherTeacher: '',
    email: order.email || '',
    payment: order.payment || '',
    bread: order.bread || '',
    cheese: order.cheese || '',
    meat: order.meat || '',
    condiments: order.condiments ? order.condiments.split(',').map(s => s.trim()).filter(Boolean) : [],
    toppings: order.toppings ? order.toppings.split(',').map(s => s.trim()).filter(Boolean) : [],
    cookie: order.cookie || '',
    side: order.side || '',
  });

  const fetchOptions = useCallback(async () => {
    try {
      const [optionsRes, teachersRes] = await Promise.all([
        axios.get(`${apiBase}/options`),
        axios.get(`${apiBase}/teachers`),
      ]);
      setOptions(optionsRes.data);
      setTeachers((teachersRes.data || []).filter(t => t.active));
    } catch (err) {
      setError('Failed to load form options');
    }
  }, [apiBase]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Once teachers have loaded, if the order's existing teacher isn't in the
  // active list for its grade, treat it as a free-typed "Other" entry so it
  // doesn't silently disappear from the form.
  useEffect(() => {
    if (isOtherTeacher) {
      setForm(f => (f.teacher === order.teacher ? { ...f, teacher: OTHER_TEACHER, otherTeacher: order.teacher } : f));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teachers]);

  const teacherOptions = teachers.filter(t => t.grade === form.grade);
  const askTeacher = !!options?.school?.ask_teacher;

  const toggleMulti = (field, value) => {
    setForm(f => {
      const has = f[field].includes(value);
      return { ...f, [field]: has ? f[field].filter(v => v !== value) : [...f[field], value] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const usingOther = form.teacher === OTHER_TEACHER;
    if (askTeacher && teacherOptions.length > 0 && !usingOther && !form.teacher) {
      setError('Teacher is required');
      return;
    }
    if (usingOther && !form.otherTeacher.trim()) {
      setError('Please enter the teacher name');
      return;
    }

    if (isCopy && !orderDateId) {
      setError('Choose an order date');
      return;
    }

    const content = {
      name: form.name,
      grade: form.grade,
      teacher: usingOther ? form.otherTeacher.trim() : form.teacher,
      is_other_teacher: usingOther,
      email: form.email,
      payment: form.payment,
      bread: form.bread,
      cheese: form.cheese,
      meat: form.meat,
      condiments: form.condiments,
      toppings: form.toppings,
      cookie: form.cookie,
      side: form.side,
    };

    setSaving(true);
    try {
      const res = isCopy
        ? await axios.post(`${apiBase}/orders`, {
            ...content,
            order_date_id: Number(orderDateId),
            send_email: sendEmail && !!form.email,
          })
        : await axios.put(`${apiBase}/orders/${order.id}`, content);
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const modalStyle = {
    backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem',
    width: '90%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
  };
  const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 'bold', fontSize: '0.85rem' };
  const inputStyle = { width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.9rem' };
  const fieldWrapStyle = { marginBottom: '0.9rem' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>
          {isCopy ? `New Order from ${order.reference_number}` : `Edit Order — ${order.reference_number}`}
        </h3>

        {!options ? (
          <p>Loading…</p>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ color: '#dc3545', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</div>
            )}

            {isCopy && (
              <OrderDateSelect
                id="copy-order-date"
                label="Order date:"
                orderDates={orderDates}
                value={orderDateId}
                onChange={setOrderDateId}
              />
            )}

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Name</label>
              <input
                style={inputStyle}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: askTeacher ? '1fr 1fr' : '1fr', gap: '1rem' }}>
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Grade</label>
                <select
                  style={inputStyle}
                  value={form.grade}
                  onChange={(e) => setForm({ ...form, grade: e.target.value, teacher: '', otherTeacher: '' })}
                  required
                >
                  <option value="">Select grade…</option>
                  {(options.grade || []).map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              {askTeacher && teacherOptions.length > 0 && (
                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Teacher</label>
                  <select
                    style={inputStyle}
                    value={form.teacher}
                    onChange={(e) => setForm({ ...form, teacher: e.target.value })}
                  >
                    <option value="">Select teacher…</option>
                    {teacherOptions.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                    <option value={OTHER_TEACHER}>Other (not listed)</option>
                  </select>
                  {form.teacher === OTHER_TEACHER && (
                    <input
                      style={{ ...inputStyle, marginTop: '0.5rem' }}
                      placeholder="Teacher name"
                      value={form.otherTeacher}
                      onChange={(e) => setForm({ ...form, otherTeacher: e.target.value })}
                    />
                  )}
                </div>
              )}
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Email</label>
              <input
                style={inputStyle}
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Bread</label>
                <select style={inputStyle} value={form.bread} onChange={(e) => setForm({ ...form, bread: e.target.value })} required>
                  <option value="">Select…</option>
                  {(options.bread || []).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Cheese</label>
                <select style={inputStyle} value={form.cheese} onChange={(e) => setForm({ ...form, cheese: e.target.value })} required>
                  <option value="">Select…</option>
                  {(options.cheese || []).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Meat</label>
                <select style={inputStyle} value={form.meat} onChange={(e) => setForm({ ...form, meat: e.target.value })} required>
                  <option value="">Select…</option>
                  {(options.meat || []).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Cookie</label>
                <select style={inputStyle} value={form.cookie} onChange={(e) => setForm({ ...form, cookie: e.target.value })} required>
                  <option value="">Select…</option>
                  {(options.cookie || []).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div style={fieldWrapStyle}>
                <label style={labelStyle}>Side</label>
                <select style={inputStyle} value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })} required>
                  <option value="">Select…</option>
                  {(options.side || []).map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Condiments</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {(options.condiments || []).map(v => (
                  <label key={v} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <input
                      type="checkbox"
                      checked={form.condiments.includes(v)}
                      onChange={() => toggleMulti('condiments', v)}
                    />
                    {v}
                  </label>
                ))}
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Toppings</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {(options.toppings || []).map(v => (
                  <label key={v} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <input
                      type="checkbox"
                      checked={form.toppings.includes(v)}
                      onChange={() => toggleMulti('toppings', v)}
                    />
                    {v}
                  </label>
                ))}
              </div>
            </div>

            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Payment Method</label>
              <select style={inputStyle} value={form.payment} onChange={(e) => setForm({ ...form, payment: e.target.value })} required>
                <option value="">Select…</option>
                {(options.payment || []).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            {isCopy && (
              <label style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input
                  type="checkbox"
                  checked={sendEmail && !!form.email}
                  disabled={!form.email}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
                Send confirmation email to the parent{!form.email && ' (no email address)'}
              </label>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{ padding: '0.5rem 1rem', backgroundColor: 'white', color: '#666', border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                {saving ? 'Saving…' : isCopy ? 'Create Order' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default EditOrderModal;
